/**
 * Workforce telemetry client.
 *
 * Buffers events in memory and flushes them in batches so the UI
 * never pays per-event network overhead. Flushes on:
 *   - 10 events buffered
 *   - 30 seconds elapsed since last flush
 *   - page visibilitychange → hidden  (sendBeacon for fire-and-forget)
 *   - explicit flush() call (used in tests)
 *
 * Safety contract:
 *   - all errors are swallowed; telemetry must never break the app
 *   - PII filtering is server-side, but the client also strips any
 *     property keys not in a small known set per event type
 *   - a per-tab session id is generated from crypto.randomUUID
 *     (or a Math.random fallback) — does NOT include any user data
 *
 * Public API:
 *   recordEvent({ eventType, eventSubtype?, properties? })
 *   flush()                          — force-send the current buffer
 *   getQueuedEventCount()            — test introspection
 *   getSessionId()                   — test introspection
 *   __resetForTests()                — clear all state (tests only)
 */

import { apiRequest } from './apiClient';

const FLUSH_AT_EVENT_COUNT = 10;
const FLUSH_INTERVAL_MS = 30_000;
const MAX_BUFFER = 100;
const ENDPOINT = '/api/telemetry/workforce';

let buffer = [];
let flushTimer = null;
let sessionId = null;
let visibilityListenerAttached = false;

function generateSessionId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID().replace(/-/g, '').slice(0, 32);
    }
  } catch {
    /* fall through to fallback */
  }
  // Math.random fallback — adequate for an opaque session correlator.
  let out = '';
  for (let i = 0; i < 4; i += 1) {
    out += Math.random().toString(36).slice(2, 10);
  }
  return out.slice(0, 32);
}

function getOrCreateSessionId() {
  if (!sessionId) {
    sessionId = generateSessionId();
  }
  return sessionId;
}

function scheduleFlush() {
  if (flushTimer || typeof window === 'undefined') return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush().catch(() => {});
  }, FLUSH_INTERVAL_MS);
}

function attachVisibilityListener() {
  if (visibilityListenerAttached || typeof document === 'undefined') return;
  visibilityListenerAttached = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushViaBeacon();
    }
  });
}

function takeBuffer() {
  if (buffer.length === 0) return [];
  const events = buffer;
  buffer = [];
  return events;
}

/**
 * Flush via fetch (returns a promise). Errors are swallowed.
 */
export async function flush() {
  const events = takeBuffer();
  if (events.length === 0) return { sent: 0 };
  try {
    await apiRequest(ENDPOINT, {
      method: 'POST',
      body: JSON.stringify({ events })
    });
    return { sent: events.length };
  } catch {
    // Telemetry never blocks UI. Dropped on the floor.
    return { sent: 0, dropped: events.length };
  }
}

/**
 * Flush via navigator.sendBeacon — fire-and-forget, browser-managed,
 * resilient to page navigation. No await, no promise.
 */
export function flushViaBeacon() {
  const events = takeBuffer();
  if (events.length === 0) return;
  try {
    if (typeof navigator === 'undefined' || !navigator.sendBeacon) return;
    const blob = new Blob([JSON.stringify({ events })], { type: 'application/json' });
    navigator.sendBeacon(ENDPOINT, blob);
  } catch {
    /* swallow */
  }
}

/**
 * Record a single event. Cheap; appends to in-memory buffer. Auto-
 * flushes when the buffer hits FLUSH_AT_EVENT_COUNT or the periodic
 * timer fires.
 */
export function recordEvent(event) {
  if (!event || !event.eventType) return;
  attachVisibilityListener();
  if (buffer.length >= MAX_BUFFER) {
    // Drop oldest to keep memory bounded; never block.
    buffer.shift();
  }
  buffer.push({
    eventType: event.eventType,
    eventSubtype: event.eventSubtype || null,
    properties: event.properties || {},
    sessionId: getOrCreateSessionId()
  });
  if (buffer.length >= FLUSH_AT_EVENT_COUNT) {
    flush().catch(() => {});
  } else {
    scheduleFlush();
  }
}

export function getQueuedEventCount() {
  return buffer.length;
}

export function getSessionId() {
  return sessionId;
}

export function __resetForTests() {
  buffer = [];
  sessionId = null;
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  visibilityListenerAttached = false;
}
